/**
 * ai/thesisEngine.ts
 *
 * Maintains stateful thesis memory per symbol.
 * Tracks thesis evolution, history, and invalidation.
 *
 * This is the "memory" of the AI trading engine.
 * It answers: what did we think, what changed, what do we think now?
 */

import { now } from '../../../utils/clock';
import { ValidatedAIResponse, AIThesisChange } from '../types/responseSchema';
import { logEvent } from '../../../features/status/systemEventLog';
import { activeThesisStorage } from './thesisStorage';

// ─── Thesis state per symbol ──────────────────────────────────────────────────

export interface SymbolThesis {
  symbol:        string;
  current:       ValidatedAIResponse | null;
  previous:      ValidatedAIResponse | null;
  history:       ThesisHistoryEntry[];       // last 10 entries
  lastUpdatedAt: number | null;
  lastInputHash: string | null;
  updateCount:   number;
  isStale:       boolean;  // true if older than staleness threshold
}

export interface ThesisHistoryEntry {
  timestamp:    number;
  conviction:   number;
  actionability: number;
  bias:         string;
  marketState:  string;
  thesisChange: AIThesisChange;
  summary:      string;    // one-line summary of this thesis
  inputHash:    string;
}

// ─── Thesis store ─────────────────────────────────────────────────────────────
// Module-level singleton (persistence via thesisStorage adapter)

const thesisStore = new Map<string, SymbolThesis>();
const listeners   = new Set<() => void>();

function notify() { listeners.forEach(fn => fn()); }

function saveThesis() {
  const obj: Record<string, SymbolThesis> = {};
  thesisStore.forEach((t, sym) => { obj[sym] = t; });
  activeThesisStorage.save(obj);
}

function loadThesis() {
  const obj = activeThesisStorage.load();
  // Only restore entries from the last 8 hours
  const cutoff = now() - 8 * 60 * 60 * 1000;
  Object.entries(obj).forEach(([sym, t]) => {
    if (!t || typeof t !== 'object' || !t.lastUpdatedAt) return;
    if (t.lastUpdatedAt > cutoff) {
      thesisStore.set(sym, { ...t, isStale: true }); // mark stale on load — needs refresh
    }
  });
}

// Load persisted theses on module init
loadThesis();

export function subscribeThesis(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ─── Get or init ──────────────────────────────────────────────────────────────

export function getThesis(symbol: string): SymbolThesis {
  if (!thesisStore.has(symbol)) {
    thesisStore.set(symbol, {
      symbol,
      current:       null,
      previous:      null,
      history:       [],
      lastUpdatedAt: null,
      lastInputHash: null,
      updateCount:   0,
      isStale:       true,
    });
  }
  return thesisStore.get(symbol)!;
}

export function getAllTheses(): Map<string, SymbolThesis> {
  return thesisStore;
}

// ─── Apply new AI response ────────────────────────────────────────────────────

export function applyAIResponse(response: ValidatedAIResponse): SymbolThesis {
  const { symbol } = response;
  const existing = getThesis(symbol);

  // Move current to previous
  const previous = existing.current;

  // Build history entry from outgoing current
  const newHistory = [...existing.history];
  if (previous) {
    const entry: ThesisHistoryEntry = {
      timestamp:     previous.timestamp,
      conviction:    previous.conviction,
      actionability: previous.actionability,
      bias:          previous.overallBias,
      marketState:   previous.marketState,
      thesisChange:  previous.thesisChange,
      summary:       summarizeThesis(previous),
      inputHash:     previous.inputHash,
    };
    newHistory.unshift(entry);
    if (newHistory.length > 10) newHistory.pop();
  }

  const updated: SymbolThesis = {
    symbol,
    current:       response,
    previous,
    history:       newHistory,
    lastUpdatedAt: now(),
    lastInputHash: response.inputHash,
    updateCount:   existing.updateCount + 1,
    isStale:       false,
  };

  thesisStore.set(symbol, updated);
  saveThesis();
  logEvent('thesis',
    `Thesis ${updated.current?.thesisChange || 'updated'} — ${updated.current?.overallBias || ''} ${updated.current?.marketState || ''}`,
    symbol);
  notify();
  return updated;
}

// ─── Mark stale ───────────────────────────────────────────────────────────────

export function markStale(symbol: string) {
  const t = getThesis(symbol);
  thesisStore.set(symbol, { ...t, isStale: true });
  notify();
}

export function markAllStale() {
  thesisStore.forEach((_, sym) => markStale(sym));
}

// ─── Confidence delta ─────────────────────────────────────────────────────────

export function getConvictionDelta(symbol: string): number | null {
  const t = getThesis(symbol);
  if (!t.current || !t.previous) return null;
  return t.current.conviction - t.previous.conviction;
}

// ─── Has state actually changed? ──────────────────────────────────────────────

export function hasStateChanged(symbol: string, newHash: string): boolean {
  const t = getThesis(symbol);
  return t.lastInputHash !== newHash;
}

// ─── Thesis invalidated? ──────────────────────────────────────────────────────

export function isThesisInvalidated(symbol: string): boolean {
  const t = getThesis(symbol);
  return t.current?.thesisChange === 'invalidated';
}

// ─── Generate a summary line ──────────────────────────────────────────────────

function summarizeThesis(r: ValidatedAIResponse): string {
  const change = r.thesisChange === 'invalidated' ? '⚠ INVALIDATED'
    : r.thesisChange === 'strengthened' ? '↑ Strengthened'
    : r.thesisChange === 'weakened' ? '↓ Weakened'
    : r.thesisChange === 'new' ? '★ New'
    : '→ Unchanged';
  return `${change} | ${r.overallBias.toUpperCase()} ${r.marketState} | Conv:${r.conviction}% Act:${r.actionability}% | ${r.bestSetupType}`;
}

export function getThesisSummary(symbol: string): string {
  const t = getThesis(symbol);
  if (!t.current) return 'No thesis yet';
  return summarizeThesis(t.current);
}

// ─── Staleness check ─────────────────────────────────────────────────────────

const STALE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

export function checkStaleness() {
  const staleCheck = now();
  thesisStore.forEach((thesis, sym) => {
    if (thesis.lastUpdatedAt && (staleCheck - thesis.lastUpdatedAt > STALE_THRESHOLD_MS)) {
      if (!thesis.isStale) {
        thesisStore.set(sym, { ...thesis, isStale: true });
        notify();
      }
    }
  });
}

// ─── Clear thesis for symbol ──────────────────────────────────────────────────

export function clearThesis(symbol: string) {
  thesisStore.delete(symbol);
  notify();
}
